import React, {useEffect, useState} from 'react';
import {Button, Dialog, Text, TextTypography} from 'wix-ui-tpa/cssVars';
import {useLocaleKeys} from '../../../locale-keys/LocaleKeys';
import {useControllerProps} from '../Widget/ControllerContext';
import {getErrorPropsFromCode, ErrorProps} from '../../../domain/utils/errors';
import {classes} from './ErrorDialog.st.css';
import {SPECS} from '../../../common/constants';
import {useExperiments} from '@wix/yoshi-flow-editor';
import {CheckoutErrorModel} from '../../../domain/models/checkout/CheckoutError.model';
import {Action, ModalErrorModel} from '../../../domain/models/checkout/Errors/ModalError.model';
import {BiErrorDialogAction} from '../../../domain/utils/bi.util';

export enum ErrorDialogDataHooks {
  Root = 'ErrorDialogDataHooks.Root',
  Title = 'ErrorDialogDataHooks.Title',
  Description = 'ErrorDialogDataHooks.Description',
  Button = 'ErrorDialogDataHooks.Button',
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export const ErrorDialog = () => {
  const {
    checkoutStore: {
      placeOrderError,
      onErrorDialogOpened,
      onErrorDialogClosed,
      reportCheckoutCartClickOnCtaInErrorDialog,
      cart,
      updateCheckoutError,
      modalError,
    },
    navigationStore: {clickOnBackToSite, clickOnReturnToCart},
  } = useControllerProps();
  const localeKeys = useLocaleKeys();
  const {experiments} = useExperiments();
  const [error] = useState(
    experiments.enabled(SPECS.NewCheckoutErrorModels)
      ? cart.noItemsError || modalError
      : cart.noItemsError || placeOrderError || updateCheckoutError
  );
  const [isDialogOpen, setIsDialogOpen] = useState(true);

  useEffect(() => {
    onErrorDialogOpened(error);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDialogOnClose = () => {
    onErrorDialogClosed();
    setIsDialogOpen(false);
  };

  const onClose = () => {
    if (experiments.enabled(SPECS.NewCheckoutErrorModels)) {
      reportCheckoutCartClickOnCtaInErrorDialog({
        errorCode: (error as ModalErrorModel)?.biCode,
        actionName: BiErrorDialogAction.clickOnX,
      });
    }
    handleDialogOnClose();
  };

  const handleClickOnReturnToCart = () => {
    void clickOnReturnToCart();
  };

  const errorToDisplay = experiments.enabled(SPECS.NewCheckoutErrorModels)
    ? error
    : getErrorPropsFromCode({
        errorCode: (error as CheckoutErrorModel)?.code,
        errorData: (error as CheckoutErrorModel)?.data,
        localeKeys,
        clickOnBackToSite,
        handleClickOnReturnToCart,
        handleDialogOnClose,
        currentCart: cart.currentCart,
      });

  const onClick = () => {
    if (!experiments.enabled(SPECS.NewCheckoutErrorModels)) {
      return (errorToDisplay as ErrorProps).action.fn();
    }
    reportCheckoutCartClickOnCtaInErrorDialog({
      errorCode: (errorToDisplay as ModalErrorModel).biCode,
      actionName: BiErrorDialogAction.clickOnCta,
    });
    if ((errorToDisplay as ModalErrorModel).action === Action.BackToSite) {
      return clickOnBackToSite();
    }
    if ((errorToDisplay as ModalErrorModel).action === Action.ReturnToCart) {
      if (!cart.currentCart) {
        return clickOnBackToSite();
      }
      return handleClickOnReturnToCart();
    }
    return handleDialogOnClose();
  };

  const getButtonText = () => {
    if (!experiments.enabled(SPECS.NewCheckoutErrorModels)) {
      return (errorToDisplay as ErrorProps).action.name;
    }
    if ((errorToDisplay as ModalErrorModel).action === Action.BackToSite) {
      return localeKeys.cart.validation.error.EMPTY_CART.button();
    }
    if ((errorToDisplay as ModalErrorModel).action === Action.ReturnToCart) {
      if (!cart.currentCart) {
        return localeKeys.cart.validation.error.EMPTY_CART.button();
      }
      return localeKeys.checkout.informative_modal.return_to_cart();
    }
    if ((errorToDisplay as ModalErrorModel).action === Action.GotIt) {
      return localeKeys.checkout.page.technicalCalculationError.cta();
    }
    return localeKeys.checkout.orderFailedModal.returnToCheckout.button();
  };

  return (
    <Dialog data-hook={ErrorDialogDataHooks.Root} isOpen={isDialogOpen} onClose={onClose}>
      <div className={classes.root}>
        <div className={classes.content}>
          <Text data-hook={ErrorDialogDataHooks.Title} typography={TextTypography.largeTitle}>
            {(errorToDisplay as ErrorProps).title}
          </Text>
          <Text data-hook={ErrorDialogDataHooks.Description} typography={TextTypography.runningText}>
            {(errorToDisplay as ErrorProps).description}
          </Text>
        </div>
        <Button data-hook={ErrorDialogDataHooks.Button} className={classes.button} onClick={() => onClick()} upgrade>
          {getButtonText()}
        </Button>
      </div>
    </Dialog>
  );
};
